import { useState } from 'react';
import { getErrorMessage } from '../api/client';
import { createVoidRequest } from '../api/voidRequests';
import type { VoidRequestTargetType, VoidRequestType } from '../types/voidRequests';
import { ClosedShiftNotice } from './ClosedShiftNotice';
import { Modal } from './Modal';

const MIN_REASON_LENGTH = 10;

/**
 * Solicitud de cambio (antes solo "anulación"): el empleado pide ANULAR o MODIFICAR un registro
 * CONFIRMED/CLOSED que él mismo creó. Nunca aplica nada directamente — crea la solicitud; un
 * MANAGER/OWNER la resuelve desde "Solicitudes": anular se aplica solo al aprobar; un cambio no se
 * aplica solo (un confirmado no se edita: se anula y se recaptura; en un corte, aprobar lo anula).
 * Compartido por Compras, Otros Gastos, Ventas Otros y Corte de Turno.
 */
export function VoidRequestModal({
  targetType,
  targetId,
  targetSummary,
  storeId,
  occurredAt,
  timeZone,
  allowModify = true,
  onClose,
  onSubmitted,
}: {
  targetType: VoidRequestTargetType;
  /** F2: una merma solo se pide ANULAR (si la cantidad estaba mal, se anula y se registra de nuevo). */
  allowModify?: boolean;
  targetId: string;
  targetSummary: string;
  // Tienda y fecha EFECTIVA del registro, solo para el aviso de corte cerrado. Opcionales: el corte
  // de turno pide su propia anulación desde aquí y ahí el aviso no aplica (el corte ES el corte).
  storeId?: string | null;
  occurredAt?: string | null;
  timeZone?: string;
  onClose: () => void;
  onSubmitted: (message: string) => Promise<void> | void;
}) {
  const [mode, setMode] = useState<VoidRequestType>('VOID');
  const [text, setText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const isModify = mode === 'MODIFY';
  const isValid = text.trim().length >= MIN_REASON_LENGTH;

  const submit = async () => {
    if (isSaving || !isValid) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const value = text.trim();
      const result = await createVoidRequest(
        isModify
          ? { targetType, targetId, type: 'MODIFY', requestedChange: value, reason: value }
          : { targetType, targetId, type: 'VOID', reason: value },
      );
      await onSubmitted(result.message);
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'No fue posible enviar la solicitud.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      // P-075 (C2): un solo nombre para lo mismo. La bandeja se llama «Solicitudes de anulación y cambio» y
      // aquí se elige entre las dos cosas; la merma solo admite anular y conserva su título.
      title={allowModify ? 'Solicitar anulación o cambio' : 'Pedir anulación'}
      description={targetSummary}
      isBusy={isSaving}
      onClose={onClose}
      footer={
        <>
          <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
          <button className="button button--primary" disabled={isSaving || !isValid} onClick={() => void submit()} type="button">
            {isSaving ? 'Enviando...' : 'Enviar solicitud'}
          </button>
        </>
      }
    >
      {error && <div className="alert alert--error">{error}</div>}
      {/* Quien pide el cambio no lo ejecuta, así que aquí el aviso no evita un error: evita una
          expectativa falsa —creer que anular el gasto va a arreglar el corte que ya entregó. */}
      <ClosedShiftNotice storeId={storeId} at={occurredAt} timeZone={timeZone} action={isModify ? 'modificar' : 'anular'} asRequest />
      {allowModify && (
        <div className="segmented" role="tablist" aria-label="Tipo de solicitud">
          <button className={`segmented__option ${!isModify ? 'segmented__option--active' : ''}`} role="tab" aria-selected={!isModify} type="button" onClick={() => setMode('VOID')}>Anular</button>
          <button className={`segmented__option ${isModify ? 'segmented__option--active' : ''}`} role="tab" aria-selected={isModify} type="button" onClick={() => setMode('MODIFY')}>Cambiar</button>
        </div>
      )}
      {/* P-075 (S15): antes prometía que el gerente «hará el cambio», pero un registro confirmado no se
          edita: lo que de verdad pasa, si procede, es anularlo y capturarlo de nuevo. */}
      <div className="alert alert--warning">
        {isModify
          ? 'El cambio no se aplica solo: un dueño o gerente revisará tu solicitud. Un registro confirmado no se edita; si procede, se anula y se captura de nuevo con los datos correctos.'
          : 'La anulación no se aplicará de inmediato. Un dueño o gerente debe aprobarla.'}
      </div>
      <label className="field">
        <span>{isModify ? `¿Qué cambio necesitas? (mínimo ${MIN_REASON_LENGTH} caracteres)` : `Motivo de la anulación (mínimo ${MIN_REASON_LENGTH} caracteres)`}</span>
        <textarea onChange={(event) => setText(event.target.value)} placeholder={isModify ? 'Ej. El monto correcto es $80, no $77.' : ''} rows={3} value={text} />
      </label>
    </Modal>
  );
}
